import { useState, useEffect } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useForm } from "react-hook-form";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { supabase } from "../integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Home, Hotel, CheckCircle } from "lucide-react";
import { FACILITIES, ROOM_TYPES } from "../constants/hostelFormFields";

export default function HostelOnboarding() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);
  const navigate = usePrefixedNavigate();
  const { user } = useAuth();

  const form = useForm();

  useEffect(() => {
    if (!user) {
      navigate("/hostel/register");
    }
  }, [user, navigate]);

  const handleNext = (data: any) => {
    setFormData({ ...formData, ...data });
    form.reset();
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
    form.reset();
  };

  const handleFinalSubmit = async (data: any) => {
    const finalData = {
      ...formData,
      ...data,
      selectedFacilities,
      selectedRoomTypes,
    };

    try {
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Create hostel
      const { data: hostelData, error: hostelError } = await supabase
        .from("hostels")
        .insert({
          name: finalData.name ?? "Unnamed Hostel",
          type: finalData.type ?? "hostel",
          hostel_type: finalData.hostel_type ?? null,
          city: finalData.city ?? null,
          state: finalData.state ?? null,
          pincode: finalData.pincode ?? null,
          address_line1: finalData.address_line1 ?? null,
          address_line2: finalData.address_line2 ?? null,
          email: finalData.email ?? null,
          phone: finalData.phone ?? null,
          website: finalData.website ?? null,
          total_capacity: finalData.total_capacity
            ? Number.parseInt(finalData.total_capacity, 10) || 0
            : 0,
          number_of_rooms: finalData.number_of_rooms
            ? Number.parseInt(finalData.number_of_rooms, 10) || null
            : null,
          room_types: selectedRoomTypes.length > 0 ? selectedRoomTypes : null,
          facilities: selectedFacilities.length > 0 ? selectedFacilities : null,
          year_established: finalData.year_established
            ? Number.parseInt(finalData.year_established, 10) || null
            : null,
          funding_needed: finalData.funding_needed
            ? Number.parseFloat(finalData.funding_needed) || null
            : null,
          funding_type: finalData.funding_type ?? null,
          monthly_operational_cost: finalData.monthly_operational_cost
            ? Number.parseFloat(finalData.monthly_operational_cost) || null
            : null,
          govt_support: finalData.govt_support === "yes",
          status: "pending",
          admin_id: user.id,
        })
        .select()
        .single();

      if (hostelError) {
        throw hostelError;
      }

      // Update profile with contact person info
      if (finalData.contact_person) {
        await supabase.from("profiles").upsert({
          id: user.id,
          full_name: finalData.contact_person,
        });
      }

      // Create trustee if provided
      if (finalData.trustee_name && hostelData) {
        await supabase.from("trustees").insert({
          name: finalData.trustee_name,
          phone: finalData.trustee_phone ?? null,
          email: finalData.trustee_email ?? null,
          designation: finalData.trustee_designation ?? null,
          hostel_id: hostelData.id,
          user_id: user.id,
        });
      }

      toast.success("Hostel registered successfully!");
      navigate("/dashboard/hostel-admin");
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(error.message ?? "Failed to register hostel");
    }
  };

  const toggleFacility = (facility: string) => {
    setSelectedFacilities(prev =>
      prev.includes(facility)
        ? prev.filter(f => f !== facility)
        : [...prev, facility]
    );
  };

  const toggleRoomType = (type: string) => {
    setSelectedRoomTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <Home className="h-5 w-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  JBF Portal
                </h1>
                <p className="text-xs text-muted-foreground">
                  Hostel Onboarding
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <Card>
            <CardHeader>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
                  <Hotel className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle>Hostel Registration</CardTitle>
                  <CardDescription>
                    Step {step} of 5 - All fields are optional
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(s => (
                  <div
                    key={s}
                    className={`h-2 flex-1 rounded-full transition-colors ${
                      s <= step ? "bg-primary" : "bg-muted"
                    }`}
                  />
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {/* Step 1: Basic Information */}
              {step === 1 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Basic Information
                  </h3>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Hostel Name</Label>
                      <Input
                        {...form.register("name")}
                        placeholder="Enter hostel name"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("type", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hostel">Hostel</SelectItem>
                          <SelectItem value="gurukul">Gurukul</SelectItem>
                          <SelectItem value="boarding">
                            Boarding School
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Hostel Type</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("hostel_type", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select hostel type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="boys">Boys</SelectItem>
                          <SelectItem value="girls">Girls</SelectItem>
                          <SelectItem value="co-ed">Co-ed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Year Established</Label>
                      <Input
                        type="number"
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e =>
                          ["e", "E", "-", "+"].includes(e.key) &&
                          e.preventDefault()
                        }
                        {...form.register("year_established")}
                        placeholder="e.g., 2010"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Contact Person Name</Label>
                    <Input
                      {...form.register("contact_person")}
                      placeholder="Enter contact person name"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        {...form.register("email")}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        {...form.register("phone")}
                        maxLength={10}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, "");
                          form.setValue("phone", value);
                        }}
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Website</Label>
                    <Input
                      {...form.register("website")}
                      placeholder="https://"
                    />
                  </div>

                  <Button type="submit" className="w-full" size="lg">
                    Continue
                  </Button>
                </form>
              )}

              {/* Step 2: Address Information */}
              {step === 2 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Address Information
                  </h3>

                  <div>
                    <Label>Address Line 1</Label>
                    <Input
                      {...form.register("address_line1")}
                      placeholder="Enter address"
                    />
                  </div>

                  <div>
                    <Label>Address Line 2</Label>
                    <Input
                      {...form.register("address_line2")}
                      placeholder="Enter address line 2"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <Label>City</Label>
                      <Input
                        {...form.register("city")}
                        placeholder="Enter city"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        {...form.register("state")}
                        placeholder="Enter state"
                      />
                    </div>
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        {...form.register("pincode")}
                        maxLength={6}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, "");
                          form.setValue("pincode", value);
                        }}
                        placeholder="Enter pincode"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Continue
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 3: Capacity & Facilities */}
              {step === 3 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Capacity & Facilities
                  </h3>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Total Student Capacity</Label>
                      <Input
                        type="number"
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e =>
                          ["e", "E", "-", "+"].includes(e.key) &&
                          e.preventDefault()
                        }
                        {...form.register("total_capacity")}
                        placeholder="Enter capacity"
                      />
                    </div>
                    <div>
                      <Label>Number of Rooms</Label>
                      <Input
                        type="number"
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e =>
                          ["e", "E", "-", "+"].includes(e.key) &&
                          e.preventDefault()
                        }
                        {...form.register("number_of_rooms")}
                        placeholder="Enter number of rooms"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Room Types</Label>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {ROOM_TYPES.map((type: string) => (
                        <div
                          key={type}
                          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${
                            selectedRoomTypes.includes(type)
                              ? "border-primary bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => toggleRoomType(type)}
                        >
                          <CheckCircle
                            className={`h-4 w-4 ${selectedRoomTypes.includes(type) ? "opacity-100" : "opacity-0"}`}
                          />
                          <span>{type}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Facilities Available</Label>
                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-5">
                      {FACILITIES.map((facility: string) => (
                        <div
                          key={facility}
                          className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                            selectedFacilities.includes(facility)
                              ? "border-primary bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => toggleFacility(facility)}
                        >
                          <CheckCircle
                            className={`h-4 w-4 ${selectedFacilities.includes(facility) ? "opacity-100" : "opacity-0"}`}
                          />
                          <span className="text-sm">{facility}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Continue
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 4: Trustee Information */}
              {step === 4 && (
                <form
                  onSubmit={form.handleSubmit(handleNext)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Trustee Information
                  </h3>

                  <div>
                    <Label>Trustee Name</Label>
                    <Input
                      {...form.register("trustee_name")}
                      placeholder="Enter trustee name"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Trustee Phone</Label>
                      <Input
                        {...form.register("trustee_phone")}
                        maxLength={10}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, "");
                          form.setValue("trustee_phone", value);
                        }}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <Label>Trustee Email</Label>
                      <Input
                        type="email"
                        {...form.register("trustee_email")}
                        placeholder="Enter email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Designation / Role</Label>
                    <Input
                      {...form.register("trustee_designation")}
                      placeholder="e.g., Chairman, Secretary"
                    />
                  </div>

                  <div>
                    <Label>Number of Trustees</Label>
                    <Input
                      type="number"
                      min={0}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e =>
                        ["e", "E", "-", "+"].includes(e.key) &&
                        e.preventDefault()
                      }
                      {...form.register("trustee_count")}
                      placeholder="Enter number of trustees"
                    />
                  </div>

                  <div>
                    <Label>Names of Other Trustees</Label>
                    <Textarea
                      {...form.register("other_trustees")}
                      placeholder="Enter names of other trustees"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Continue
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 5: Funding Information */}
              {step === 5 && (
                <form
                  onSubmit={form.handleSubmit(handleFinalSubmit)}
                  className="space-y-4"
                >
                  <h3 className="mb-4 text-lg font-semibold">
                    Funding Information
                  </h3>

                  <div>
                    <Label>Funding Required For</Label>
                    <Input
                      {...form.register("funding_purpose")}
                      placeholder="e.g., Infrastructure, Equipment"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Approximate Amount Needed (₹)</Label>
                      <Input
                        type="number"
                        min={0}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e =>
                          ["e", "E", "-", "+"].includes(e.key) &&
                          e.preventDefault()
                        }
                        {...form.register("funding_needed")}
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <Label>Funding Type</Label>
                      <Select
                        onValueChange={(value: string) =>
                          form.setValue("funding_type", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select funding type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="donation">Donation</SelectItem>
                          <SelectItem value="grant">Grant</SelectItem>
                          <SelectItem value="loan">Loan</SelectItem>
                          <SelectItem value="csr">CSR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Monthly Operational Expenses (₹)</Label>
                    <Input
                      type="number"
                      min={0}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e =>
                        ["e", "E", "-", "+"].includes(e.key) &&
                        e.preventDefault()
                      }
                      {...form.register("monthly_operational_cost")}
                      placeholder="Enter amount"
                    />
                  </div>

                  <div>
                    <Label>Government Aid Received</Label>
                    <Select
                      onValueChange={(value: string) =>
                        form.setValue("govt_support", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Support Already Received</Label>
                    <Textarea
                      {...form.register("support_received")}
                      placeholder="Describe any support already received"
                    />
                  </div>

                  <div>
                    <Label>Additional Comments</Label>
                    <Textarea
                      {...form.register("comments")}
                      placeholder="Any additional information..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" size="lg">
                      Submit Registration
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-muted-foreground">
          © 2024 JBF Portal. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
